import { Controller } from '@hotwired/stimulus';
import { submitFeedback } from '../../utils/case_experience/case_experience_in_rails';

export default class extends Controller {
  static targets = ['ratingInput', 'issuesInput', 'commentInput', 'submitButton'];

  static values = {
    threadId: Number,
    context: String,
    documentId: Number,
    nodeUid: String,
  };

  connect() {
    this._previousFocus = this._resolvePreviousFocus();
    this._onEscape = this._handleEscape.bind(this);
    this._onEmoticonSelected = this._handleEmoticonSelected.bind(this);
    this._disableRailsuiEscape();
    this._bindEscape();
    this.element.addEventListener('badge-emoticons:selected', this._onEmoticonSelected);
  }

  disconnect() {
    this._unbindEscape();
    this.element.removeEventListener('badge-emoticons:selected', this._onEmoticonSelected);
    this._restorePreviousFocus();
  }

  handleCheckboxKeydown(event) {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.checked = !event.currentTarget.checked;
      this.updateIssues();
    }
  }

  updateIssues() {
    const checkedIssues = Array.from(this.element.querySelectorAll('.issue-checkbox:checked')).map(
      (cb) => cb.value,
    );
    this.issuesInputTarget.value = JSON.stringify(checkedIssues);
  }

  updateSubmitButtonState() {
    if (!this.hasSubmitButtonTarget) return;
    const hasRating = Boolean(this.ratingInputTarget?.value);
    this.submitButtonTarget.disabled = !hasRating;
  }

  cancel() {
    this.element.dispatchEvent(new CustomEvent('vpe-feedback-modal:close', { bubbles: true }));
  }

  async submit() {
    if (!this.hasSubmitButtonTarget || this.submitButtonTarget.disabled) return;

    const rating = parseInt(this.ratingInputTarget.value, 10);
    if (Number.isNaN(rating)) return;

    let issues = [];
    try {
      issues = this.issuesInputTarget.value
        ? JSON.parse(this.issuesInputTarget.value)
        : Array.from(this.element.querySelectorAll('.issue-checkbox:checked')).map((cb) => cb.value);
    } catch {
      issues = [];
    }
    const comment = this.hasCommentInputTarget ? this.commentInputTarget.value.trim() : '';

    this.submitButtonTarget.disabled = true;

    try {
      await submitFeedback(this.threadIdValue, rating, issues, comment);
      this.element.dispatchEvent(
        new CustomEvent('vpe-feedback-modal:submitted', {
          bubbles: true,
          detail: {
            context: this.contextValue,
            threadId: this.threadIdValue,
            rating,
            issues,
            comment,
          },
        }),
      );
    } catch (error) {
      console.error(error);
      this.submitButtonTarget.disabled = false;
    }
  }

  get isOpen() {
    const container = this.element.querySelector('[data-railsui-modal-target="container"]');
    return Boolean(container && !container.classList.contains('hidden'));
  }

  _handleEmoticonSelected(event) {
    const input = event.detail?.input;
    if (!(input instanceof HTMLInputElement)) return;

    this._syncRatingFromInput(input);
  }

  _syncRatingFromInput(input) {
    const rating = parseInt(this._ratingParamFrom(input), 10);
    if (Number.isNaN(rating)) return;

    this.ratingInputTarget.value = String(rating);
    this.updateSubmitButtonState();
  }

  _handleEscape(event) {
    if (event.key !== 'Escape' || !this.element.isConnected || !this.isOpen) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.cancel();
  }

  _bindEscape() {
    this._unbindEscape();
    window.addEventListener('keyup', this._onEscape, true);
  }

  _unbindEscape() {
    window.removeEventListener('keyup', this._onEscape, true);
  }

  _disableRailsuiEscape() {
    const container = this.element.querySelector('[data-railsui-modal-target="container"]');
    if (!container) return;

    const action = container.getAttribute('data-action') || '';
    const filtered = action
      .split(/\s+/)
      .filter((entry) => entry && !entry.includes('closeWithEsc'))
      .join(' ')
      .trim();

    if (filtered) container.setAttribute('data-action', filtered);
    else container.removeAttribute('data-action');
  }

  _resolvePreviousFocus() {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active !== document.body) {
      return active;
    }

    return null;
  }

  _ratingParamFrom(input) {
    return (
      input.getAttribute('data-case-experience--vpe-feedback-modal-rating-param') ||
      input.dataset.caseExperienceVpeFeedbackModalRatingParam
    );
  }

  _restorePreviousFocus() {
    if (
      this._previousFocus &&
      document.contains(this._previousFocus) &&
      typeof this._previousFocus.focus === 'function'
    ) {
      this._previousFocus.focus();
    }

    this._previousFocus = null;
  }
}
